import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

// =============================================================================
// Types
// =============================================================================

/**
 * Status of a pending completion
 */
export type CompletionStatus = 'pending' | 'completed' | 'failed';

/**
 * Generic optimistic data type. Each module stores its own shape and casts
 * it when consuming. Using Record<string, unknown> keeps the slice
 * module-agnostic.
 */
export type GenericOptimisticData = Record<string, unknown>;

/**
 * A shipment that is in the process of being completed (Draft → InProgress)
 */
export interface PendingCompletion {
  /** Shipment nvxId */
  nvxId: string;
  /** Timestamp when completion was started */
  startedAt: number;
  /** Current status of the completion process */
  status: CompletionStatus;
  /** Error message if completion failed */
  error?: string;
  /** Optimistic data to display while completion is pending (module-specific shape) */
  optimisticData?: GenericOptimisticData;
}

/**
 * Slice state
 */
export interface PendingCompletionsState {
  /** Map of nvxId to pending completion */
  completions: Record<string, PendingCompletion>;
}

// =============================================================================
// Initial State
// =============================================================================

const initialState: PendingCompletionsState = {
  completions: {},
};

// =============================================================================
// Slice
// =============================================================================

export const pendingCompletionsSlice = createSlice({
  name: 'pendingCompletions',
  initialState,
  reducers: {
    /**
     * Add a new pending completion with optional optimistic data
     */
    addPendingCompletion: (
      state,
      action: PayloadAction<{ nvxId: string; optimisticData?: GenericOptimisticData }>
    ) => {
      const { nvxId, optimisticData } = action.payload;
      state.completions[nvxId] = {
        nvxId,
        startedAt: Date.now(),
        status: 'pending',
        optimisticData,
      };
    },

    /**
     * Mark a completion as successful
     */
    markCompletionDone: (state, action: PayloadAction<{ nvxId: string }>) => {
      const { nvxId } = action.payload;
      if (state.completions[nvxId]) {
        state.completions[nvxId].status = 'completed';
      }
    },

    /**
     * Mark a completion as failed
     */
    markCompletionFailed: (
      state,
      action: PayloadAction<{ nvxId: string; error?: string }>
    ) => {
      const { nvxId, error } = action.payload;
      if (state.completions[nvxId]) {
        state.completions[nvxId].status = 'failed';
        state.completions[nvxId].error = error;
      }
    },

    /**
     * Remove a pending completion (cleanup after UI has shown the result)
     */
    removePendingCompletion: (state, action: PayloadAction<{ nvxId: string }>) => {
      const { nvxId } = action.payload;
      delete state.completions[nvxId];
    },

    /**
     * Clear all pending completions (e.g., on logout)
     */
    clearAllPendingCompletions: (state) => {
      state.completions = {};
    },

    /**
     * Clear stale pending completions (older than specified age in ms)
     * Default: 10 minutes
     */
    clearStalePendingCompletions: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      const maxAge = action.payload ?? 10 * 60 * 1000; // 10 minutes default
      const now = Date.now();
      for (const nvxId of Object.keys(state.completions)) {
        if (now - state.completions[nvxId].startedAt > maxAge) {
          delete state.completions[nvxId];
        }
      }
    },
  },
});

// =============================================================================
// Actions
// =============================================================================

export const {
  addPendingCompletion,
  markCompletionDone,
  markCompletionFailed,
  removePendingCompletion,
  clearAllPendingCompletions,
  clearStalePendingCompletions,
} = pendingCompletionsSlice.actions;

// =============================================================================
// Selectors
// =============================================================================

/** Select all pending completions */
export const selectPendingCompletions = (state: RootState) =>
  state.pendingCompletions.completions;

/** Select pending completions that are still in 'pending' status (for polling) */
export const selectActivePendingCompletions = createSelector(
  [selectPendingCompletions],
  (completions): PendingCompletion[] =>
    Object.values(completions).filter((c) => c.status === 'pending')
);

/** Check if a specific shipment is pending completion */
export const selectIsPendingCompletion =
  (nvxId: string) =>
  (state: RootState): boolean =>
    state.pendingCompletions.completions[nvxId]?.status === 'pending';

/** Get a specific pending completion by nvxId */
export const selectPendingCompletionByNvxId =
  (nvxId: string) =>
  (state: RootState): PendingCompletion | undefined =>
    state.pendingCompletions.completions[nvxId];

/** Get optimistic data for a specific shipment */
export const selectOptimisticDataByNvxId =
  (nvxId: string) =>
  (state: RootState): GenericOptimisticData | undefined =>
    state.pendingCompletions.completions[nvxId]?.optimisticData;

export default pendingCompletionsSlice.reducer;
